import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { DollarSign, Package, Building2, ArrowLeft, TrendingUp, FileText, Send, CheckCircle, Clock } from 'lucide-react';

const FinanceManagement = () => {
  const [royaltyData, setRoyaltyData] = useState(null);
  const [kitData, setKitData] = useState(null);
  const [showRoyaltyList, setShowRoyaltyList] = useState(false);
  const [showKitList, setShowKitList] = useState(false);
  const [centersWithRoyalty, setCentersWithRoyalty] = useState([]);
  const [centersWithKit, setCentersWithKit] = useState([]);
  const [royaltyInvoices, setRoyaltyInvoices] = useState([]);
  const [showInvoicesDialog, setShowInvoicesDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);
  
  // New states for centers outstanding
  const [centersOutstanding, setCentersOutstanding] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [centerDetails, setCenterDetails] = useState(null);
  const [showCenterDetails, setShowCenterDetails] = useState(false);

  useEffect(() => {
    loadFinanceData();
    loadRoyaltyInvoices();
    loadCentersOutstanding();
  }, []);

  const loadCentersOutstanding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/finance/centers-outstanding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCentersOutstanding(response.data);
    } catch (error) {
      console.error('Error loading centers outstanding:', error);
      toast.error('Failed to load centers outstanding');
    }
  };

  const loadCenterDetails = async (centerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/finance/center-outstanding-details/${centerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCenterDetails(response.data);
      setShowCenterDetails(true);
    } catch (error) {
      console.error('Error loading center details:', error);
      toast.error('Failed to load center details');
    }
  };

  const sendCenterReminder = async (centerId, centerName) => {
    try {
      setSendingReminder(centerId);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/finance/send-reminder/${centerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reminder sent to ${centerName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const loadRoyaltyInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/royalty-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoyaltyInvoices(response.data);
    } catch (error) {
      console.error('Error loading royalty invoices:', error);
    }
  };

  const sendReminder = async (invoiceId) => {
    try {
      setSendingReminder(invoiceId);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/royalty-invoices/${invoiceId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reminder sent to ${response.data.center_name}`);
      loadRoyaltyInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const markAsPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid? This action will update all associated student invoices.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/royalty-invoices/${invoiceId}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice marked as paid successfully');
      loadRoyaltyInvoices();
      loadFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark invoice as paid');
    }
  };

  const loadFinanceData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Load all invoices
      const invoicesResponse = await axios.get(`${API}/fee-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const invoices = invoicesResponse.data;

      // Load all kit requests
      const kitResponse = await axios.get(`${API}/kit-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const kits = kitResponse.data;

      // Load all centers
      const centersResponse = await axios.get(`${API}/centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const centers = centersResponse.data;

      // Calculate Pending Royalty Amount
      const pendingRoyaltyInvoices = invoices.filter(inv => inv.status === 'paid' && inv.royalty_paid === false);
      const totalPendingRoyalty = pendingRoyaltyInvoices.reduce((sum, inv) => sum + (inv.royalty_amount || 0), 0);

      // Group by center
      const royaltyByCenter = {};
      pendingRoyaltyInvoices.forEach(inv => {
        if (!royaltyByCenter[inv.center_id]) {
          const center = centers.find(c => c.id === inv.center_id);
          royaltyByCenter[inv.center_id] = {
            center_id: inv.center_id,
            center_name: center?.name || 'Unknown',
            center_location: center?.location || 'N/A',
            total_pending: 0,
            invoice_count: 0
          };
        }
        royaltyByCenter[inv.center_id].total_pending += inv.royalty_amount || 0;
        royaltyByCenter[inv.center_id].invoice_count += 1;
      });

      setRoyaltyData({
        total_pending: totalPendingRoyalty,
        invoice_count: pendingRoyaltyInvoices.length
      });
      setCentersWithRoyalty(Object.values(royaltyByCenter));

      // Calculate Pending Kit Amount
      const pendingKits = kits.filter(kit => 
        kit.status === 'approved' && 
        kit.payment_status === 'pending'
      );
      const totalPendingKit = pendingKits.reduce((sum, kit) => sum + (kit.kit_amount || 0), 0);

      // Group by center
      const kitByCenter = {};
      pendingKits.forEach(kit => {
        if (!kitByCenter[kit.center_id]) {
          const center = centers.find(c => c.id === kit.center_id);
          kitByCenter[kit.center_id] = {
            center_id: kit.center_id,
            center_name: center?.name || 'Unknown',
            center_location: center?.location || 'N/A',
            total_pending: 0,
            kit_count: 0
          };
        }
        kitByCenter[kit.center_id].total_pending += kit.kit_amount || 0;
        kitByCenter[kit.center_id].kit_count += 1;
      });

      setKitData({
        total_pending: totalPendingKit,
        kit_count: pendingKits.length
      });
      setCentersWithKit(Object.values(kitByCenter));

    } catch (error) {
      console.error('Error loading finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading finance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Finance Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">Track pending royalty and kit payments from centers</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pending Royalty Card */}
          <Card 
            className="p-6 border-2 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-purple-50 to-indigo-100 border-purple-200"
            onClick={() => setShowRoyaltyList(true)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-4 bg-purple-500 rounded-full shadow-lg">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-600 font-semibold uppercase tracking-wide">Pending Royalty</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">
                  ₹{royaltyData?.total_pending.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-purple-200">
              <div>
                <p className="text-sm text-purple-700 font-medium">From Centers</p>
                <p className="text-xs text-purple-600">{centersWithRoyalty.length} centers with pending payments</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-700 font-medium">{royaltyData?.invoice_count || 0} Invoices</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 bg-white hover:bg-purple-50 text-purple-700 border-purple-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRoyaltyList(true);
                  }}
                >
                  View Details →
                </Button>
              </div>
            </div>
          </Card>

          {/* Pending Kit Amount Card */}
          <Card 
            className="p-6 border-2 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-blue-50 to-cyan-100 border-blue-200"
            onClick={() => setShowKitList(true)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-4 bg-blue-500 rounded-full shadow-lg">
                <Package className="w-8 h-8 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-600 font-semibold uppercase tracking-wide">Pending Kit Amount</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">
                  ₹{kitData?.total_pending.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-blue-200">
              <div>
                <p className="text-sm text-blue-700 font-medium">From Centers</p>
                <p className="text-xs text-blue-600">{centersWithKit.length} centers with pending payments</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-700 font-medium">{kitData?.kit_count || 0} Kits</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 bg-white hover:bg-blue-50 text-blue-700 border-blue-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowKitList(true);
                  }}
                >
                  View Details →
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Total Pending</p>
                <p className="text-lg font-bold text-gray-900">
                  ₹{((royaltyData?.total_pending || 0) + (kitData?.total_pending || 0)).toFixed(2)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Centers with Pending Royalty</p>
                <p className="text-lg font-bold text-gray-900">{centersWithRoyalty.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Centers with Pending Kit</p>
                <p className="text-lg font-bold text-gray-900">{centersWithKit.length}</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowInvoicesDialog(true)}
          >
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-indigo-700 font-semibold">Royalty Invoices</p>
                <p className="text-lg font-bold text-indigo-900">{royaltyInvoices.length}</p>
                <p className="text-xs text-indigo-600 mt-1">View & Send Reminders</p>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* Centers Outstanding Section */}
      <Card className="p-6 shadow-xl border-0">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            Centers Outstanding Payments
          </h3>
          <p className="text-sm text-gray-600 mt-1">Track royalty and kit payments pending from each center</p>
        </div>

        <div className="space-y-4">
          {centersOutstanding.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p>No outstanding payments from any center</p>
            </div>
          ) : (
            centersOutstanding.map((center) => (
              <Card 
                key={center.center_id} 
                className={`p-5 border-2 hover:shadow-lg transition-all cursor-pointer ${
                  center.total_pending > 0 ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
                }`}
                onClick={() => center.total_pending > 0 && loadCenterDetails(center.center_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {center.center_name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg text-gray-900">{center.center_name}</h4>
                      <p className="text-sm text-gray-600">{center.center_location}</p>
                      
                      <div className="flex items-center gap-6 mt-3">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-purple-600" />
                          <div>
                            <p className="text-xs text-gray-500">Royalty</p>
                            <p className="text-sm font-semibold text-purple-700">
                              ₹{center.royalty_amount.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-xs text-gray-500">Kit Amount</p>
                            <p className="text-sm font-semibold text-blue-700">
                              ₹{center.kit_amount.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right ml-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Total Pending</p>
                      <p className={`text-2xl font-bold ${
                        center.total_pending > 0 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        ₹{center.total_pending.toFixed(2)}
                      </p>
                    </div>
                    
                    {center.total_pending > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          sendCenterReminder(center.center_id, center.center_name);
                        }}
                        disabled={sendingReminder === center.center_id}
                      >
                        {sendingReminder === center.center_id ? (
                          <Clock className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Reminder
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </Card>

      {/* Center Details Dialog */}
      <Dialog open={showCenterDetails} onOpenChange={setShowCenterDetails}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Building2 className="w-6 h-6" />
              {centerDetails?.center.name} - Outstanding Details
            </DialogTitle>
          </DialogHeader>
          
          {centerDetails && (
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <p className="text-sm text-purple-600 font-semibold">Total Royalty</p>
                  <p className="text-2xl font-bold text-purple-900">
                    ₹{centerDetails.summary.total_royalty.toFixed(2)}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    {centerDetails.royalty_invoices.length} invoice(s)
                  </p>
                </Card>
                
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold">Total Kit Amount</p>
                  <p className="text-2xl font-bold text-blue-900">
                    ₹{centerDetails.summary.total_kit.toFixed(2)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {centerDetails.kit_requests.length} kit(s)
                  </p>
                </Card>
                
                <Card className="p-4 bg-orange-50 border-orange-200">
                  <p className="text-sm text-orange-600 font-semibold">Total Outstanding</p>
                  <p className="text-2xl font-bold text-orange-900">
                    ₹{centerDetails.summary.total_outstanding.toFixed(2)}
                  </p>
                </Card>
              </div>

              {/* Royalty Invoices */}
              {centerDetails.royalty_invoices.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    Pending Royalty Invoices
                  </h4>
                  <div className="space-y-2">
                    {centerDetails.royalty_invoices.map((invoice) => (
                      <Card key={invoice.id} className="p-4 border border-purple-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{invoice.invoice_number}</p>
                            <p className="text-sm text-gray-600">
                              {invoice.month}/{invoice.year}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-900">
                              ₹{invoice.royalty_amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {invoice.royalty_percentage}% royalty
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Kit Requests */}
              {centerDetails.kit_requests.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Pending Kit Payments
                  </h4>
                  <div className="space-y-2">
                    {centerDetails.kit_requests.map((kit) => (
                      <Card key={kit.id} className="p-4 border border-blue-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">Kit for {kit.student_name}</p>
                            <p className="text-sm text-gray-600">
                              Requested: {new Date(kit.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              Status: {kit.status}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-blue-900">
                              ₹{kit.total_amount ? kit.total_amount.toFixed(2) : '0.00'}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Royalty List Dialog */}
      <Dialog open={showRoyaltyList} onOpenChange={setShowRoyaltyList}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <DollarSign className="w-5 h-5" />
              Centers with Pending Royalty Amount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
              <Send className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-purple-800">
                <span className="font-semibold">Send reminders</span> to centers for pending royalty payments
              </p>
            </div>

            {centersWithRoyalty.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No pending royalty payments</p>
            ) : (
              centersWithRoyalty.map((center, idx) => {
                // Find corresponding royalty invoice for this center
                const centerInvoice = royaltyInvoices.find(inv => 
                  inv.center_id === center.center_id && inv.status === 'pending'
                );

                return (
                  <Card key={idx} className="p-5 border-2 border-purple-100 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl">
                          {center.center_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 text-lg">{center.center_name}</h4>
                          <p className="text-sm text-gray-600">{center.center_location}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <p className="text-xs text-purple-600 font-medium">
                              📋 {center.invoice_count} paid invoices
                            </p>
                            {centerInvoice && centerInvoice.reminder_sent && (
                              <p className="text-xs text-orange-600">
                                ✉️ {centerInvoice.reminder_count} reminder(s) sent
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-xs text-gray-600">Pending Amount</p>
                        <p className="text-3xl font-bold text-purple-700 mb-3">₹{center.total_pending.toFixed(2)}</p>
                        
                        {centerInvoice ? (
                          <Button
                            onClick={() => sendReminder(centerInvoice.id)}
                            disabled={sendingReminder === centerInvoice.id}
                            size="sm"
                            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-lg"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {sendingReminder === centerInvoice.id ? 'Sending...' : 'Send Reminder'}
                          </Button>
                        ) : (
                          <p className="text-xs text-gray-500 italic">
                            Invoice will be generated on 10th
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Kit Amount List Dialog */}
      <Dialog open={showKitList} onOpenChange={setShowKitList}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Package className="w-5 h-5" />
              Centers with Pending Kit Amount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {centersWithKit.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No pending kit payments</p>
            ) : (
              centersWithKit.map((center, idx) => (
                <Card key={idx} className="p-4 border-2 border-blue-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold">
                        {center.center_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{center.center_name}</h4>
                        <p className="text-sm text-gray-600">{center.center_location}</p>
                        <p className="text-xs text-blue-600 mt-1">{center.kit_count} approved kits</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600">Pending Amount</p>
                      <p className="text-2xl font-bold text-blue-700">₹{center.total_pending.toFixed(2)}</p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Royalty Invoices Dialog */}
      <Dialog open={showInvoicesDialog} onOpenChange={setShowInvoicesDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <FileText className="w-5 h-5" />
              Royalty Invoices - View & Send Reminders
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-indigo-50 p-3 rounded-lg border border-indigo-200">
              <Clock className="w-5 h-5 text-indigo-600" />
              <p className="text-sm text-indigo-800">
                <span className="font-semibold">Auto-generated on 10th</span> of each month for previous month's paid invoices
              </p>
            </div>

            {royaltyInvoices.length === 0 ? (
              <p className="text-center text-gray-500 py-12">No royalty invoices generated yet</p>
            ) : (
              <div className="space-y-3">
                {royaltyInvoices.map((invoice) => (
                  <Card key={invoice.id} className={`p-5 border-2 transition-shadow hover:shadow-lg ${
                    invoice.status === 'paid' 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-orange-200 bg-orange-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold shadow-lg ${
                          invoice.status === 'paid' 
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-br from-orange-500 to-red-600'
                        }`}>
                          <FileText className="w-7 h-7" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-bold text-gray-900 text-lg">{invoice.invoice_number}</h4>
                            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                              invoice.status === 'paid' 
                                ? 'bg-green-200 text-green-800' 
                                : 'bg-orange-200 text-orange-800'
                            }`}>
                              {invoice.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-700 font-semibold mb-1">{invoice.center_name}</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                            <div>
                              <p className="text-xs text-gray-600">Billing Period</p>
                              <p className="font-semibold text-gray-900">
                                {new Date(invoice.billing_year, invoice.billing_month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Students</p>
                              <p className="font-semibold text-gray-900">{invoice.student_count}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Generated</p>
                              <p className="font-semibold text-gray-900">
                                {new Date(invoice.generated_date).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Due Date</p>
                              <p className="font-semibold text-red-700">
                                {new Date(invoice.due_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          {invoice.reminder_sent && (
                            <p className="text-xs text-orange-600 mt-2">
                              ✉️ {invoice.reminder_count} reminder(s) sent • Last: {new Date(invoice.last_reminder_date).toLocaleDateString()}
                            </p>
                          )}
                          {invoice.status === 'paid' && invoice.paid_date && (
                            <p className="text-xs text-green-600 mt-2">
                              ✓ Paid on {new Date(invoice.paid_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-xs text-gray-600">Amount</p>
                        <p className="text-3xl font-bold text-indigo-900">₹{invoice.total_royalty_amount.toFixed(2)}</p>
                        <div className="flex gap-2 mt-3">
                          {invoice.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => sendReminder(invoice.id)}
                                disabled={sendingReminder === invoice.id}
                                size="sm"
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                {sendingReminder === invoice.id ? 'Sending...' : 'Send Reminder'}
                              </Button>
                              <Button
                                onClick={() => markAsPaid(invoice.id)}
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Mark Paid
                              </Button>
                            </>
                          )}
                          {invoice.status === 'paid' && (
                            <div className="flex items-center gap-2 text-green-600 font-bold">
                              <CheckCircle className="w-5 h-5" />
                              Paid
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceManagement;
